import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'

/**
 * Conversation Explorer service layer — the read-only Admin (Management) API
 * endpoints that back the page. Called through the chat-api wrapper as app_system.
 * See docs/conversation-explorer/dashboard-design.md §1 (ENG-36887 / ENG-36888).
 *
 * `app` is the untyped legacy app object (id, accessKey, appRegion, …); the API
 * responses are typed at the call sites that consume them, so services return
 * `Promise<any>` and map errors to `error.response.data`.
 */

type Affix = 'append' | 'prepend'

export interface ListConversationsOptions {
  limit?: number
  conversationType?: 'user' | 'group'
  cursor?: string
  affix?: Affix
  withTags?: boolean
  /** UID sent as the `onBehalfOf` header — scopes the list to that user's own conversations. */
  onBehalfOf?: string
}

/**
 * ENG-36887 — GET /v3/conversations?unique=1
 * One row per conversation, ordered updatedAt DESC (server-deduped). Cursor-paginated.
 * `unique=1` is the super-admin app-wide mode and the API rejects it alongside the
 * `onBehalfOf` header, so passing a UID swaps to that user's own conversation list.
 */
export async function listUniqueConversations(
  app: any,
  apiHost: string,
  options: ListConversationsOptions = {},
): Promise<any> {
  const { limit = 50, conversationType, cursor, affix, withTags, onBehalfOf } = options
  let URL = `${getChatApiBase(app, apiHost)}/conversations?${
    onBehalfOf ? '' : 'unique=1&'
  }limit=${encodeURIComponent(limit)}`
  if (conversationType) URL += `&conversationType=${encodeURIComponent(conversationType)}`
  if (cursor) URL += `&cursor=${encodeURIComponent(cursor)}`
  if (affix) URL += `&affix=${encodeURIComponent(affix)}`
  if (withTags) URL += `&withTags=1`

  const headers: Record<string, string> = { ...getChatApiHeaders(app) }
  if (onBehalfOf) headers.onBehalfOf = onBehalfOf

  return axios
    .get(URL, { headers })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export interface ListMessagesOptions {
  limit?: number
  id?: string
  sentAt?: number
  affix?: Affix
  categories?: string
  types?: string
  searchKey?: string
  hideReplies?: boolean
}

/**
 * ENG-36888 — GET /v3/conversations/{conversationId}/messages
 * Messages for one conversation. Soft-deleted messages are INCLUDED by default.
 * Default page = NEWEST messages. Page OLDER via meta.previous { id, sentAt, affix:'prepend' };
 * page NEWER via meta.next { id, sentAt, affix:'append' }.
 */
export async function listConversationMessages(
  app: any,
  apiHost: string,
  conversationId: string,
  options: ListMessagesOptions = {},
): Promise<any> {
  const {
    limit = 50,
    id,
    sentAt,
    affix,
    categories,
    types,
    searchKey,
    hideReplies = true,
  } = options
  let URL = `${getChatApiBase(app, apiHost)}/conversations/${encodeURIComponent(
    conversationId,
  )}/messages?limit=${encodeURIComponent(limit)}`
  if (affix) URL += `&affix=${encodeURIComponent(affix)}`
  if (id) URL += `&id=${encodeURIComponent(id)}`
  if (sentAt) URL += `&sentAt=${encodeURIComponent(sentAt)}`
  if (categories) URL += `&categories=${encodeURIComponent(categories)}`
  if (types) URL += `&types=${encodeURIComponent(types)}`
  if (searchKey) URL += `&searchKey=${encodeURIComponent(searchKey)}`
  // Thread replies live under their parent (fetched via listMessageThread), so
  // keep them out of the main timeline — otherwise they'd also show flat.
  if (hideReplies) URL += `&hideReplies=1`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/**
 * GET /v3/conversations/{conversationId} — the single conversation, carrying its
 * CURRENT `lastMessage` (freshens the left-panel row, which is otherwise frozen at
 * initial list-load time). Returns `{ data: [...] }` with one entry per participant/
 * member — all share the same `conversationId` + `lastMessage`, so callers take
 * `data[0]`. Routed through the mgmt-api wrapper (Bearer only; the region key is
 * injected server-side), so no raw apiKey reaches the browser.
 *
 * NOTE: the `lastMessage` here uses the MESSAGES-endpoint shape (top-level
 * `deletedAt`/`editedAt`), NOT the list endpoint's action-wrapper shape — consumers
 * must handle both.
 */
export async function getConversation(
  app: any,
  apiHost: string,
  conversationId: string,
): Promise<any> {
  const URL = `${getChatApiBase(app, apiHost)}/conversations/${encodeURIComponent(conversationId)}`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export interface ListThreadOptions {
  limit?: number
  id?: string
  sentAt?: number
  affix?: Affix
}

/**
 * GET /v3/messages/{id}/thread — the reply messages of a threaded parent.
 * Same message shape as the conversation thread; cursor-paginated via meta.next.
 * A parent's reply count is on `message.replyCount`.
 */
export async function listMessageThread(
  app: any,
  apiHost: string,
  messageId: string,
  options: ListThreadOptions = {},
): Promise<any> {
  const { limit = 50, id, sentAt, affix } = options
  let URL = `${getChatApiBase(app, apiHost)}/messages/${encodeURIComponent(
    messageId,
  )}/thread?limit=${encodeURIComponent(limit)}`
  if (affix) URL += `&affix=${encodeURIComponent(affix)}`
  if (id) URL += `&id=${encodeURIComponent(id)}`
  if (sentAt) URL += `&sentAt=${encodeURIComponent(sentAt)}`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export interface ListReactionsOptions {
  limit?: number
  id?: string
  affix?: Affix
}

/**
 * GET /v3/messages/{id}/reactions — individual reactors for a message.
 * Each row: { reaction, uid, reactedAt, reactedBy: { uid, name, avatar, ... } }.
 * Cursor-paginated via `meta.next` (id/affix). Backs the right-panel Reactions list.
 */
export async function listMessageReactions(
  app: any,
  apiHost: string,
  messageId: string,
  options: ListReactionsOptions = {},
): Promise<any> {
  const { limit = 100, id, affix } = options
  let URL = `${getChatApiBase(app, apiHost)}/messages/${encodeURIComponent(
    messageId,
  )}/reactions?limit=${encodeURIComponent(limit)}`
  if (affix) URL += `&affix=${encodeURIComponent(affix)}`
  if (id) URL += `&id=${encodeURIComponent(id)}`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/**
 * GET /v3/messages/{id} — message detail. We read its per-member receipts
 * (`data.receipts.data[]` = { deliveredAt, readAt, recipient }) to power the
 * group Read By / Delivered to lists.
 */
export async function getMessageReceipts(
  app: any,
  apiHost: string,
  messageId: string,
): Promise<any> {
  const URL = `${getChatApiBase(app, apiHost)}/messages/${encodeURIComponent(messageId)}`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export interface ListFlaggedOptions {
  limit?: number
  conversationId?: string
  messageId?: string
  cursor?: string
  affix?: Affix
}

/**
 * GET /v3/moderation/flagged-messages — user-report records (right-panel Moderation).
 * Each row carries `flaggedBy: [{uid, name, avatar, reasonId, reasonName, remark, flaggedAt}]`
 * + `flaggedCount`; the right panel joins these to the selected message by id.
 */
export async function listFlaggedMessages(
  app: any,
  apiHost: string,
  options: ListFlaggedOptions = {},
): Promise<any> {
  const { limit = 100, conversationId, messageId, cursor, affix } = options
  let URL = `${getChatApiBase(app, apiHost)}/moderation/flagged-messages?limit=${encodeURIComponent(
    limit,
  )}`
  if (conversationId) URL += `&conversationId=${encodeURIComponent(conversationId)}`
  // Filter to a single message when supported, so the right-panel flag data is
  // always current for the selected message (avoids the paginated-list miss).
  if (messageId) URL += `&messageId=${encodeURIComponent(messageId)}`
  if (cursor) URL += `&cursor=${encodeURIComponent(cursor)}`
  if (affix) URL += `&affix=${encodeURIComponent(affix)}`

  return axios
    .get(URL, { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
